import { useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";

interface DeleteResourceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  resource: {
    id: string;
    title: string;
    filename: string;
  } | null;
  onSuccess: () => void;
}

export const DeleteResourceDialog = ({
  open,
  onOpenChange,
  resource,
  onSuccess,
}: DeleteResourceDialogProps) => {
  const { toast } = useToast();
  const [action, setAction] = useState<'replace' | 'delete' | null>(null);
  const [replacementFile, setReplacementFile] = useState<File | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const handleReplace = async () => {
    if (!resource || !replacementFile) return;

    setIsProcessing(true);
    try {
      // Validate file size
      if (replacementFile.size > 10 * 1024 * 1024) {
        toast({
          title: "File too large",
          description: "Maximum file size is 10MB.",
          variant: "destructive",
        });
        return;
      }

      // Validate file type
      const allowedTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'text/plain'
      ];
      
      if (!allowedTypes.includes(replacementFile.type)) {
        toast({
          title: "Invalid file type",
          description: "Only PDF, DOC, DOCX, and TXT files are allowed.",
          variant: "destructive",
        });
        return;
      }

      // Delete old file
      const { error: deleteError } = await supabase.storage
        .from('resources')
        .remove([resource.filename]);

      if (deleteError) throw deleteError;

      // Upload new file with same name
      const { error: uploadError } = await supabase.storage
        .from('resources')
        .upload(resource.filename, replacementFile, {
          upsert: true,
        });

      if (uploadError) throw uploadError;

      // Update the updated_at timestamp in database
      const { error: updateError } = await supabase
        .from('resources')
        .update({ updated_at: new Date().toISOString() })
        .eq('id', resource.id);

      if (updateError) throw updateError;

      toast({
        title: "Success!",
        description: "Resource file replaced successfully.",
      });

      onSuccess();
      onOpenChange(false);
      setAction(null);
      setReplacementFile(null);
    } catch (error: any) {
      console.error('Error replacing resource:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to replace resource.",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDelete = async () => {
    if (!resource) return;

    setIsProcessing(true);
    try {
      // Delete file from storage
      const { error: storageError } = await supabase.storage
        .from('resources')
        .remove([resource.filename]);

      if (storageError) throw storageError;

      // Delete record from database
      const { error: dbError } = await supabase
        .from('resources')
        .delete()
        .eq('id', resource.id);

      if (dbError) throw dbError;

      toast({
        title: "Success!",
        description: "Resource deleted permanently.",
      });

      onSuccess();
      onOpenChange(false);
      setAction(null);
    } catch (error: any) {
      console.error('Error deleting resource:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to delete resource.",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  if (!action) {
    // Step 1: Choose action
    return (
      <AlertDialog open={open} onOpenChange={onOpenChange}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{resource?.title}"?</AlertDialogTitle>
            <AlertDialogDescription>
              Choose how you want to proceed:
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex flex-col gap-3 py-4">
            <Button
              variant="outline"
              className="w-full justify-start"
              onClick={() => setAction('replace')}
            >
              <span className="font-semibold">Replace File Only</span>
              <span className="text-xs text-muted-foreground ml-2">
                Keep title, description, etc. Just update the file.
              </span>
            </Button>
            <Button
              variant="destructive"
              className="w-full justify-start"
              onClick={() => setAction('delete')}
            >
              <span className="font-semibold">Delete Completely</span>
              <span className="text-xs text-muted-foreground ml-2">
                Remove file and all metadata permanently.
              </span>
            </Button>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    );
  }

  if (action === 'replace') {
    // Step 2a: Replace file
    return (
      <AlertDialog open={open} onOpenChange={onOpenChange}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Replace File for "{resource?.title}"</AlertDialogTitle>
            <AlertDialogDescription>
              Upload a new file to replace the existing one. The title, description, and other metadata will remain unchanged.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <Label htmlFor="replacement-file">New File *</Label>
            <Input
              id="replacement-file"
              type="file"
              accept=".pdf,.doc,.docx,.txt"
              onChange={(e) => setReplacementFile(e.target.files?.[0] || null)}
              disabled={isProcessing}
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setAction(null);
              setReplacementFile(null);
            }} disabled={isProcessing}>
              Cancel
            </AlertDialogCancel>
            <Button
              onClick={handleReplace}
              disabled={!replacementFile || isProcessing}
            >
              {isProcessing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Replacing...
                </>
              ) : (
                "Replace File"
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    );
  }

  // Step 2b: Confirm deletion
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
          <AlertDialogDescription>
            This will permanently delete "{resource?.title}" including the file and all metadata. 
            This action cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={() => setAction(null)} disabled={isProcessing}>
            Cancel
          </AlertDialogCancel>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={isProcessing}
          >
            {isProcessing ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Deleting...
              </>
            ) : (
              "Delete Permanently"
            )}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
